import React from 'react';
import { annotate } from 'rough-notation';
import type { RoughAnnotation, RoughAnnotationConfig } from 'rough-notation/lib/model';

export type RoughType = 'underline' | 'circle';

export type RoughOptions = {
    type: RoughType;
    color?: string;
    strokeWidth?: number;
    padding?: RoughAnnotationConfig['padding'];
    animationDuration?: number;
    iterations?: number;
    multiline?: boolean;
    /* Hold the mark back until the rest of the choreography has landed. */
    delay?: number;
};

function readInk(el: Element) {
    const value = getComputedStyle(el).getPropertyValue('--ink-800').trim();
    return value || '#161B24';
}

function prefersReducedMotion() {
    return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/* Draws a rough-notation mark on `ref` the first time it scrolls into view.
   The mark is an absolutely positioned SVG sibling, so it never affects layout. */
export function useRoughNotation(ref: React.RefObject<HTMLElement | null>, options: RoughOptions) {
    const {
        type,
        color,
        strokeWidth = 2.5,
        padding = 4,
        animationDuration = 800,
        iterations = 2,
        multiline = false,
        delay = 0,
    } = options;

    React.useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const reduced = prefersReducedMotion();
        let annotation: RoughAnnotation | null = annotate(el, {
            type,
            color: color ?? readInk(el),
            strokeWidth,
            padding,
            iterations,
            multiline,
            animate: !reduced,
            animationDuration,
        });

        let shown = false;
        let raf = 0;
        let timer = 0;
        const reveal = () => {
            if (shown || !annotation) return;
            shown = true;
            /* One frame of slack so the mark measures settled layout, not the
               geometry mid-reveal-transition. */
            const draw = () => {
                raf = requestAnimationFrame(() => annotation?.show());
            };
            if (delay && !reduced) timer = window.setTimeout(draw, delay);
            else draw();
        };

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    reveal();
                    observer.disconnect();
                }
            },
            { rootMargin: '-10% 0px -10% 0px' },
        );
        observer.observe(el);

        return () => {
            cancelAnimationFrame(raf);
            window.clearTimeout(timer);
            observer.disconnect();
            annotation?.remove();
            annotation = null;
        };
    }, [ref, type, color, strokeWidth, padding, animationDuration, iterations, multiline, delay]);
}
